/**
 * Auth flow: signInWithGoogle → trade Firebase ID token for backend JWT.
 */
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from "firebase/auth";

import { api } from "@/lib/api";
import { getFirebaseAuth, googleProvider } from "@/lib/firebase";
import { useAuth, type AuthUser, type AuthTokens } from "@/stores/auth";

interface SignInResponse {
  user: AuthUser;
  tokens: Omit<AuthTokens, "expires_at">;
}

export async function signInWithGoogle(): Promise<AuthUser> {
  const auth = getFirebaseAuth();
  const result = await signInWithPopup(auth, googleProvider);
  const firebaseUser: FirebaseUser = result.user;
  const idToken = await firebaseUser.getIdToken(/* forceRefresh */ true);

  // Exchange Firebase ID token for backend JWT
  const { data } = await api.post<SignInResponse>("/v1/auth/sign-in", {
    firebase_id_token: idToken,
  });

  useAuth.getState().setSession(data.user, data.tokens);
  return data.user;
}

export async function signOut(): Promise<void> {
  try {
    // Notify backend (stateless mas registra log)
    await api.post("/v1/auth/sign-out").catch(() => {
      /* ignore */
    });
  } finally {
    useAuth.getState().clear();
    try {
      await firebaseSignOut(getFirebaseAuth());
    } catch {
      /* ignore — Firebase may not be initialized */
    }
  }
}

export async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const { data } = await api.get<AuthUser>("/v1/auth/me");
    useAuth.getState().setUser(data);
    return data;
  } catch {
    return null;
  }
}
